import React, { useCallback, useMemo, useState } from "react";
import SwitcherLogo from "assets/SwitcherPlayerIconGradient.svg?react";
import { Toggle } from "components/inputs/toggle/Toggle";
import styles from "../index.module.scss";
import { Button } from "components/buttons/Button";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import {
    updateGeneralForm,
    updateVideoPlayerForm
} from "store/platforms/slice";
import { PlayerSelectModal } from "components/modal/PlayerSelectModal";

export const CatalogDestination = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const platforms = useSelector((state: RootState) => state.platforms);

    const { showInCatalog, playlistIds } = useMemo(
        () => ({
            showInCatalog: platforms?.general?.generalForm?.showInCatalog,
            playlistIds:
                platforms?.videoplayer?.videoPlayerForm?.playlistIds ?? []
        }),
        [platforms]
    );

    const collectionIdsToAddTo = useMemo(
        () =>
            playlistIds?.filter(
                (id) => id && id !== "00000000-0000-0000-0000-000000000000"
            ) ?? [],
        [playlistIds]
    );

    const collectionSelectText = useMemo(
        () =>
            collectionIdsToAddTo.length > 0
                ? t("video-upload:collection-count", {
                      count: collectionIdsToAddTo.length,
                      plural:
                          collectionIdsToAddTo.length === 1
                              ? ""
                              : t("video-upload:plural")
                  })
                : t("video-upload:add-to-collections"),
        [collectionIdsToAddTo.length, t]
    );

    const [collectionsModalOpen, setCollectionsModalOpen] = useState(false);

    const handleCollectionModalOpen = useCallback(() => {
        // Launch collection selection modal
        setCollectionsModalOpen(true);
    }, []);

    const toggleShowInCatalog = useCallback(() => {
        dispatch(updateGeneralForm({ showInCatalog: !showInCatalog }));
    }, [dispatch, showInCatalog]);

    const handleSelectCollections = useCallback(
        (selectedIds: string[]) => {
            // Update the Redux state with the selected collection IDs
            dispatch(updateVideoPlayerForm({ playlistIds: selectedIds }));
            setCollectionsModalOpen(false);
        },
        [dispatch]
    );

    return (
        <div className={styles["catalog-destination"]}>
            <div className={styles["top-row"]}>
                <SwitcherLogo />
                <header>
                    <h6>{t("page-titles:catalog")}</h6>
                    <p>{t("catalog-destination:description")}</p>
                </header>
                <Toggle on={showInCatalog} onToggle={toggleShowInCatalog} />
            </div>
            <div className={styles["bottom-row"]}>
                <h6>{t("page-titles:collections")}</h6>
                <Button type="badge" onClick={handleCollectionModalOpen}>
                    {collectionSelectText}
                </Button>
            </div>
            <PlayerSelectModal
                isOpen={collectionsModalOpen}
                setIsOpen={setCollectionsModalOpen}
                previouslySelectedIds={collectionIdsToAddTo}
                handleSelect={handleSelectCollections}
                allowUnselect={true}
                allowNoSelection={true}
                returnPlaylistIds={true}
                emptyStateMessage={
                    <p>{t("catalog-destination:no-collections")}</p>
                }
            />
        </div>
    );
};
